<template>
  <form class="email-auth-form" @submit.prevent="handleSubmit">
    <label class="email-auth-label">
      <span class="email-auth-label-text"
        >{{ s__('Welcome|Email') }}<span v-if="emailRequired">&nbsp;*</span></span
      >
      <input
        ref="emailInputRef"
        :value="email"
        class="email-auth-input"
        type="email"
        :required="emailRequired"
        @input="handleEmailInput"
        @invalid="handleEmailInvalid"
      />
    </label>
    <label class="email-auth-label">
      {{ s__('Welcome|Username') }} *
      <input
        :value="username"
        class="email-auth-input"
        type="text"
        maxlength="64"
        required
        @input="event => emit('update:username', event.target.value.trim())"
      />
    </label>
    <label class="email-auth-label">
      {{ s__('Welcome|Full name') }} *
      <input
        :value="fullName"
        class="email-auth-input"
        type="text"
        maxlength="255"
        required
        @input="event => emit('update:fullName', event.target.value.trim())"
      />
    </label>
    <div class="email-auth-form-actions">
      <button class="email-auth-submit" type="submit" :disabled="disabled">
        {{ submitting ? s__('Welcome|Creating account...') : s__('Welcome|Create account') }}
      </button>
    </div>
  </form>
</template>

<script setup>
import { ref } from 'vue'
import { s__ } from '@/locale'

const props = defineProps({
  email: {
    type: String,
    default: '',
  },
  username: {
    type: String,
    default: '',
  },
  fullName: {
    type: String,
    default: '',
  },
  emailRequired: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  submitting: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:email', 'update:username', 'update:fullName', 'submit'])

const emailInputRef = ref(null)

const emailValidationMessage = input => {
  if (!input) return ''
  if (input.validity.valueMissing) {
    return s__('Welcome|Please enter your email address.')
  }
  if (input.validity.typeMismatch) {
    return s__('Welcome|Please enter a valid email address.')
  }
  return ''
}

const applyEmailCustomValidity = input => {
  if (!(input instanceof HTMLInputElement)) return
  if (!props.emailRequired) {
    input.setCustomValidity('')
    return
  }
  input.setCustomValidity(emailValidationMessage(input))
}

const handleEmailInvalid = event => {
  applyEmailCustomValidity(event?.target)
}

const handleEmailInput = event => {
  const input = event?.target
  if (!(input instanceof HTMLInputElement)) return
  emit('update:email', input.value.trim())
  input.setCustomValidity('')
  if (!input.validity.valid && props.emailRequired) {
    applyEmailCustomValidity(input)
  }
}

const handleSubmit = () => {
  const emailInput = emailInputRef.value
  if (emailInput instanceof HTMLInputElement && props.emailRequired) {
    applyEmailCustomValidity(emailInput)
    if (!emailInput.checkValidity()) {
      emailInput.reportValidity()
      return
    }
  }
  emit('submit')
}
</script>

<style scoped>
.email-auth-form {
  display: grid;
  gap: 10px;
}

.email-auth-label {
  color: #333;
  display: grid;
  gap: 6px;
  font-size: 14px;
}

.email-auth-label-text {
  display: inline-flex;
  white-space: nowrap;
}

.email-auth-input {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  min-height: 36px;
  padding: 6px 10px;
}

.email-auth-form-actions {
  display: flex;
  gap: 8px;
}

.email-auth-submit {
  border: 1px solid #2aabee;
  background: #2aabee;
  color: #fff;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
}

.email-auth-submit:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

@media (prefers-color-scheme: dark) {
  .email-auth-label {
    color: #e2e8f0;
  }

  .email-auth-input {
    background: #2d3748;
    border-color: #4a5568;
    color: #e2e8f0;
  }
}
</style>
